import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AssetsService } from 'app/core/services/assets/assets.service';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import _ from 'lodash';
import { Observable, Subject, concat, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';


@Component({
  selector: 'app-service-task-form',
  templateUrl: './service-task-form.component.html',
  styleUrls: ['./service-task-form.component.scss']
})
export class ServiceTaskFormComponent implements OnInit {

  @Input() options: any;
  @Output() drawerClose: EventEmitter<any> = new EventEmitter();

  listValue$: Observable<any[]> = new Observable<any[]>();
  valueSubject$: Subject<string | null> = new Subject<string | null>();
  taskFieldObject: any = {
    url: '/assets/inspection_form/master',
    dataKey: 'asset_form_uid',
    displayFormat: '{{asset_form_name}}',
    bindValue: 'asset_form_uid',
  };
  searchTerm: string = '';
  preloadedValue: any = [];
  lookupLoading: boolean;
  pageSize: number = 1;

  taskGroup: UntypedFormGroup;
  
  constructor(
    public utilityService: UtilityService,
    private _assetService: AssetsService,
    private _dataCollectorService: DataCollectorService
  ) { }

  closeDrawer(): void {
    this.drawerClose.emit('close');
  }

  inspectionChange(): void {
    if(this.taskGroup.get('hasInspection').value){
      if(!this.taskGroup.get('inspection').hasValidator(Validators.required)){
        this.taskGroup.get('inspection').setValidators([Validators.required]);
      }
    }else{
      if(this.taskGroup.get('inspection').hasValidator(Validators.required)){
        this.taskGroup.get('inspection').removeValidators(Validators.required);
      }
    }
    this.taskGroup.get('inspection').updateValueAndValidity();
  }

  hasValidator(): boolean{
    return this.taskGroup.get('inspection').hasValidator(Validators.required);
  }

  getLookupValueKey(): string {
    return this.taskFieldObject.bindValue;
  }

  getDurationError(name: string): string {
    if(this.taskGroup.controls[name].hasError('max') || this.taskGroup.controls[name].hasError('min')){
      if(name === 'hours'){
        return this.utilityService.getLocaleValue('customer.error.sla.hours');
      }else if(name === 'minutes'){
        return this.utilityService.getLocaleValue('customer.error.sla.minutes');
      }else if (name === 'days'){
        return this.utilityService.getLocaleValue('customer.error.sla.days');
      }
    }
  }

  constructLookupList(fieldObject, data): any {
    const template = _.template(fieldObject.displayFormat);
    const list = data.map((key) => {
      key.displayValue = template(key);
      return key;
    });
    return list;
  }

  appendValueToItems(): void {
    this.pageSize = this.pageSize + 1;
    this.lookupLoading = true;
    this.setPreLoadedValueToLookUp();
  }

  setPreLoadedValueToLookUp(): any {
    const fieldObject = this.taskFieldObject;
    const dataUrl = this._assetService.getInspectionForms();
    const paramObj = {
      'page': this.pageSize,
      'filter.keyword': this.searchTerm,
    };
    return this._dataCollectorService
      .getData(dataUrl, paramObj)
      .subscribe((response: any) => {
        this.preloadedValue = this.preloadedValue.length
          ? [
              ...this.preloadedValue,
              ...this.constructLookupList(fieldObject, response.data),
            ]
          : this.constructLookupList(fieldObject, response.data);
        this.lookupLoading = false;
        this.loadLookupValues();
      });
  }

  setListValue(term = ''): any {
    if([null,undefined].includes(term)){
      this.lookupLoading = false;
      return of(null);
    }
    const dataUrl = this._assetService.getInspectionForms();
    const paramObj = {
      'page': this.pageSize,
      'filter.keyword': term,
    };
    return this._dataCollectorService.getData(dataUrl, paramObj).pipe(
      map((response: any) => {
        this.searchTerm = term;
        this.preloadedValue = this.constructLookupList(this.taskFieldObject, response.data);
        return this.constructLookupList(this.taskFieldObject, response.data);
      })
    );
  }

  getRequiredDurationError(formGroup: UntypedFormGroup, formField: string): string{
    if(formGroup?.controls[formField]?.hasError('required') && formGroup?.controls[formField]?.touched){
      return this.utilityService.getLocaleValue('text.field_is_required');
    }
  }

  submitCustom(): void {
    this.taskGroup.markAllAsTouched();
    if(this.taskGroup.status === 'INVALID'){
      return;
    }
    const payload = {};
    payload['service_task_title'] = this.taskGroup.get('title').value || '';
    payload['service_task_description'] = this.taskGroup.get('description').value || '';
    payload['estimated_duration'] = {
      'days': this.taskGroup.get('days').value || 0,
      'hours': this.taskGroup.get('hours').value || 0,
      'minutes': this.taskGroup.get('minutes').value || 0
    };
    if(this.taskGroup.get('hasInspection').value){
      payload['inspection_form'] = this.preloadedValue.find((form: any) => this.taskGroup.get('inspection').value === form?.asset_form_uid) ;
    }else{
      payload['inspection_form'] = null;
    }

    if(this.taskGroup.get('cloneInspectionForm').value === true){
      payload['clone_from_inspection_submission_uid'] = this.options?.data?.asset_inspection_submission_uid;
    }
    const data = {
      state: this.options?.state,
      data: payload
    };
    this.drawerClose.emit(data);
  }

  prefillFormValue(data: any): void {
    this.taskGroup.get('title').setValue(data?.service_task_title);
    this.taskGroup.get('description').setValue(data?.service_task_description);
    this.taskGroup.get('days').setValue(data?.estimated_duration?.days);
    this.taskGroup.get('hours').setValue(data?.estimated_duration?.hours);
    this.taskGroup.get('minutes').setValue(data?.estimated_duration?.minutes);
    if(data?.inspection_form){
      this.taskGroup.get('hasInspection').setValue(true);
      this.taskGroup.get('inspection').setValue(data?.inspection_form?.asset_form_uid);
      this.taskGroup.get('cloneInspectionForm').setValue(this.showCloneInspection);
      this.inspectionChange();
    }
  }

  ngOnInit(): void {
    this.taskGroup = new UntypedFormGroup({
      'title': new UntypedFormControl('',[Validators.required]),
      'description': new UntypedFormControl(''),
      'days': new UntypedFormControl('', [Validators.min(0), Validators.required]),
      'hours': new UntypedFormControl(0, [Validators.min(0), Validators.max(23), Validators.required]),
      'minutes': new UntypedFormControl(0, [Validators.min(0), Validators.max(59), Validators.required]),
      'hasInspection': new UntypedFormControl(false),
      'cloneInspectionForm': new UntypedFormControl(false),
      'inspection': new UntypedFormControl(null)
    });

    this.setPreLoadedValueToLookUp();
    if(this.options?.data){
      this.prefillFormValue(this.options?.data);
    }
  }

  inspectionFormValueChange(event): any{
    this.taskGroup.get('cloneInspectionForm').setValue(this.showCloneInspection);
  }

  get showCloneInspection(): boolean{
    return this.taskGroup?.value?.hasInspection === true &&
           !this.utilityService.isEmptyOrUndefined(this.options?.data?.inspection_form?.asset_form_uid) &&
           (this.options?.data?.inspection_form?.asset_form_uid  === this.taskGroup?.value?.inspection) &&
           !this.utilityService.isEmptyOrUndefined(this.options?.data?.asset_inspection_submission_uid);
  }

  private loadLookupValues(): any {
    this.lookupLoading = false;
    this.listValue$ = concat(
      of(this.preloadedValue),
      this.valueSubject$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => (this.lookupLoading = true)),
        tap(() => (this.pageSize = 1)),
        switchMap((term: any) =>
          this.setListValue(term).pipe(
            catchError(() => of(this.preloadedValue)),
            tap(() => (this.lookupLoading = false))
          )
        )
      )
    );
  }

}
